import { useCallback } from 'react';
import { createUuid } from '~/_shared/utils/createUuid';
import { notNull } from '~/_shared/utils/typeGuards';
import type { ModalProps } from '~/modal/modalType.enum';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { ChartType } from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import { useMapSettingsCharts } from '~/store/mapSettings/data/charts/useMapSettingsCharts';
import { useManageStats } from '~/store/stats/hooks/useManageStats';
import { createEmptyBoundaryStatItem } from '~/store/stats/stats.factory';
import { chartEditorMetricToChartMetric } from '../chartEditor/chartEditor.helpers';
import {
  ChartEditorModalContainer, type ChartEditorModalContainerProps,
} from '../chartEditor/chartEditorModal.container';

export const AddNewChartModalContainer = ({
  boundaryGroupId,
  ...restProps
}: ModalProps<{
  boundaryGroupId?: number;
}>) => {
  const boundaryGroupsSelector = useBoundaryGroupsSelector();
  const { addOrUpdateChart } = useMapSettingsCharts();
  const { calculateStats } = useManageStats();

  const defaultName = boundaryGroupId
    ? boundaryGroupsSelector.find(bg => bg.id === boundaryGroupId)?.name || undefined
    : undefined;

  const handleSave: ChartEditorModalContainerProps['onSave'] = useCallback((params) => {
    const finalBoundaryGroupId = boundaryGroupId ? boundaryGroupId : params.boundaryGroupId;

    if (!finalBoundaryGroupId) {
      return;
    }

    const statItemsAndChartMetrics = params.metrics.map(metric => {
      const chartMetric = chartEditorMetricToChartMetric(metric);

      if (!chartMetric) {
        return null;
      }

      return {
        statItem: createEmptyBoundaryStatItem({
          boundaryGroupId: finalBoundaryGroupId,
          chartMetric,
        }),
        chartMetric,
      };
    }).filter(notNull);

    calculateStats(statItemsAndChartMetrics.map(item => item.statItem));

    addOrUpdateChart({
      id: createUuid(),
      title: params.name,
      shouldNormalize: true,
      metrics: statItemsAndChartMetrics.map(item => item.chartMetric),
      type: ChartType.BoundaryStatistics,
      boundaryGroupId: finalBoundaryGroupId,
    });

  }, [addOrUpdateChart, boundaryGroupId, calculateStats]);

  return (
    <ChartEditorModalContainer
      {...restProps}
      defaultName={defaultName}
      showBoundaryGroupSelector={!boundaryGroupId}
      onSave={handleSave}
    />
  );
};
